import React, { useState } from 'react';
import { Modal, Space, Button, Typography, Divider, Alert, Card, Input } from 'antd';
import {
    FacebookOutlined,
    LinkedinOutlined,
    WhatsAppOutlined,
    SendOutlined,
    TwitterOutlined,
    MailOutlined,
    CopyOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    LockOutlined,
    ShareAltOutlined,
    QrcodeOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import env from '../../env';
import AuctionQRCodeModal from './AuctionQRCodeModal';

const { Text, Title } = Typography;

const ShareAuctionModal = ({ visible, onClose, auction }) => {
    const [qrModalVisible, setQrModalVisible] = useState(false);
    const auctionUrl = `${env.FRONTEND_URL}/auctions/${auction?.slug}`;
    const shareTitle = `Check out this auction: ${auction?.title}`;

    const shareOptions = [
        {
            key: 'facebook',
            icon: <FacebookOutlined style={{ color: '#1877F2' }} />,
            label: 'Facebook',
            onClick: () => {
                const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(auctionUrl)}`;
                window.open(url, '_blank', 'width=600,height=400');
            }
        },
        {
            key: 'linkedin',
            icon: <LinkedinOutlined style={{ color: '#0A66C2' }} />,
            label: 'LinkedIn',
            onClick: () => {
                const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(auctionUrl)}`;
                window.open(url, '_blank', 'width=600,height=400');
            }
        },
        {
            key: 'twitter',
            icon: <TwitterOutlined style={{ color: '#1DA1F2' }} />,
            label: 'Twitter',
            onClick: () => {
                const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(auctionUrl)}`;
                window.open(url, '_blank', 'width=600,height=400');
            }
        },
        {
            key: 'whatsapp',
            icon: <WhatsAppOutlined style={{ color: '#25D366' }} />,
            label: 'WhatsApp',
            onClick: () => {
                const url = `https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${auctionUrl}`)}`;
                window.open(url, '_blank');
            }
        },
        {
            key: 'telegram',
            icon: <SendOutlined style={{ color: '#0088cc' }} />,
            label: 'Telegram',
            onClick: () => {
                const url = `https://t.me/share/url?url=${encodeURIComponent(auctionUrl)}&text=${encodeURIComponent(shareTitle)}`;
                window.open(url, '_blank');
            }
        },
        {
            key: 'email',
            icon: <MailOutlined style={{ color: '#EA4335' }} />,
            label: 'Email',
            onClick: () => {
                const url = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`Check out this auction: ${auctionUrl}`)}`;
                window.open(url);
            }
        },
        {
            key: 'qrcode',
            icon: <QrcodeOutlined style={{ color: '#8b5cf6' }} />,
            label: 'QR Code',
            onClick: () => {
                setQrModalVisible(true);
            }
        }
    ];

    const handleCopyLink = () => {
        navigator.clipboard.writeText(auctionUrl).then(() => {
            message.success('Link copied to clipboard!');
        }).catch(() => {
            message.error('Failed to copy link');
        });
    };

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width="90%"
            style={{ maxWidth: '600px', padding: 0 }}
            closable={true}
            maskClosable={true}
            className="share-auction-modal"
        >
            <div style={{ 
                background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                borderRadius: '12px',
                overflow: 'hidden'
            }}>
                {/* Header Section */}
                <div className="share-modal-header" style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    padding: '20px 24px 16px 24px',
                    textAlign: 'center',
                    borderBottom: 'none'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        margin: '0 auto 12px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        <ShareAltOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <Title level={4} style={{ 
                        color: 'white', 
                        margin: 0,
                        fontWeight: 600,
                        fontSize: '20px'
                    }}>
                        Share Auction
                    </Title>
                    <span style={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        fontSize: '13px',
                        display: 'block',
                        marginTop: '6px'
                    }}>
                        {auction?.title}
                    </span>
                </div>

                {/* Content Section */}
                <div className="share-modal-content" style={{ padding: '24px' }}>
                    <div className="promotional-alert" style={{ 
                        marginBottom: '24px',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #91d5ff',
                        background: '#e6f7ff'
                    }}>
                        <div className="promotional-alert-content" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: '8px'
                        }}>
                            <div style={{ fontSize: '24px', color: '#1890ff' }}>
                                <InfoCircleOutlined />
                            </div>
                            <span style={{ fontSize: '14px', fontWeight: 600, display: 'block' }}>
                                Promotional Sharing
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                <span style={{ fontSize: '13px', display: 'block' }}>
                                    These sharing options are for promoting your auction on social media or via messaging apps. These are NOT auction joining links.
                                </span>
                                {auction?.is_private && (
                                    <span style={{ fontSize: '12px', display: 'block', marginTop: 4, color: '#8c8c8c' }}>
                                        💡 For private auctions, use the "Invite Participants" or "Generate Direct Link" options from the auction menu to create proper joining links.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Share Options Grid */}
                    <div style={{ marginBottom: '24px' }}>
                        <span style={{ 
                            fontSize: '14px', 
                            color: '#262626', 
                            fontWeight: 500,
                            display: 'block',
                            marginBottom: '16px'
                        }}>
                            Share on Social Media
                        </span>
                        <div className="share-options-grid" style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(3, 1fr)', 
                            gap: '12px' 
                        }}>
                            {shareOptions.map(option => (
                                <Card
                                    key={option.key}
                                    hoverable
                                    onClick={option.onClick}
                                    style={{ 
                                        borderRadius: '8px',
                                        border: '1px solid #e6f7ff',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        padding: '12px'
                                    }}
                                    bodyStyle={{ padding: '16px 12px' }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#1890ff';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.15)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#e6f7ff';
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>
                                        {option.icon}
                                    </div>
                                    <span style={{ fontSize: '13px', fontWeight: 500, display: 'block' }}>
                                        {option.label}
                                    </span>
                                </Card>
                            ))}
                        </div>
                    </div>

                    {/* Copy Link Section */}
                    <div className="copy-link-section" style={{ 
                        padding: '16px',
                        background: '#f0f8ff',
                        borderRadius: '8px',
                        border: '1px solid #d6e4ff'
                    }}>
                        <span style={{ 
                            fontSize: '13px', 
                            color: '#262626', 
                            fontWeight: 500,
                            display: 'block',
                            marginBottom: '12px'
                        }}>
                            Copy Auction Link
                        </span>
                        <Space.Compact style={{ width: '100%' }}>
                            <Input
                                value={auctionUrl}
                                readOnly
                                style={{
                                    borderRadius: '6px 0 0 6px',
                                    borderRight: 'none'
                                }}
                            />
                            <Button
                                type="primary"
                                icon={<CopyOutlined />}
                                onClick={handleCopyLink}
                                style={{
                                    borderRadius: '0 6px 6px 0',
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    border: 'none'
                                }}
                            >
                                Copy
                            </Button>
                        </Space.Compact>
                    </div>
                </div>
            </div>

            {/* QR Code Modal */}
            {auction && (
                <AuctionQRCodeModal
                    visible={qrModalVisible}
                    onClose={() => setQrModalVisible(false)}
                    auction={auction}
                />
            )}

            <style>{`
                .share-auction-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .share-auction-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .share-auction-modal .ant-modal-close:hover {
                    color: white;
                }
                
                @media (max-width: 767px) {
                    .share-auction-modal .ant-modal {
                        width: 95% !important;
                        max-width: 95% !important;
                        margin: 10px auto !important;
                    }
                    
                    .share-modal-header {
                        padding: 16px 20px 12px 20px !important;
                    }
                    
                    .share-modal-header h4.ant-typography {
                        font-size: 18px !important;
                    }
                    
                    .share-modal-header span {
                        font-size: 12px !important;
                    }
                    
                    .share-modal-content {
                        padding: 20px 16px !important;
                    }
                    
                    .promotional-alert {
                        padding: 12px !important;
                    }
                    
                    .promotional-alert-content {
                        gap: 6px !important;
                    }
                    
                    .promotional-alert-content > div:first-child {
                        font-size: 20px !important;
                    }
                    
                    .promotional-alert-content > span {
                        font-size: 13px !important;
                    }
                    
                    .promotional-alert-content > div:last-child span {
                        font-size: 12px !important;
                    }
                    
                    .share-options-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                        gap: 10px !important;
                    }
                    
                    .share-options-grid .ant-card-body {
                        padding: 12px 8px !important;
                    }
                    
                    .share-options-grid .anticon {
                        font-size: 24px !important;
                    }
                    
                    .share-options-grid span {
                        font-size: 12px !important;
                    }
                    
                    .copy-link-section {
                        padding: 12px !important;
                    }
                    
                    .copy-link-section .ant-space-compact {
                        flex-direction: column !important;
                    }
                    
                    .copy-link-section .ant-input {
                        border-radius: 6px 6px 0 0 !important;
                        border-right: 1px solid #d9d9d9 !important;
                    }
                    
                    .copy-link-section .ant-btn {
                        border-radius: 0 0 6px 6px !important;
                        width: 100% !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .share-auction-modal .ant-modal {
                        width: 98% !important;
                        max-width: 98% !important;
                    }
                    
                    .share-modal-header {
                        padding: 12px 16px 10px 16px !important;
                    }
                    
                    .share-modal-header h4.ant-typography {
                        font-size: 16px !important;
                    }
                    
                    .share-modal-content {
                        padding: 16px 12px !important;
                    }
                    
                    .promotional-alert {
                        padding: 10px !important;
                    }
                    
                    .promotional-alert-content {
                        gap: 4px !important;
                    }
                    
                    .promotional-alert-content > div:first-child {
                        font-size: 18px !important;
                    }
                    
                    .promotional-alert-content > span {
                        font-size: 12px !important;
                    }
                    
                    .promotional-alert-content > div:last-child span {
                        font-size: 11px !important;
                    }
                    
                    .share-options-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                        gap: 8px !important;
                    }
                    
                    .share-options-grid .ant-card-body {
                        padding: 10px 6px !important;
                    }
                    
                    .share-options-grid .anticon {
                        font-size: 20px !important;
                    }
                    
                    .share-options-grid span {
                        font-size: 11px !important;
                    }
                }
            `}</style>
        </Modal>
    );
};

export default ShareAuctionModal; 